import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { analytics, EVENTS } from '../../lib/analytics'
import {
  Box, Typography, Card, Stack, Chip, Avatar,
  CircularProgress, Alert, TextField, MenuItem, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
  FormControlLabel, IconButton, Tooltip,
} from '@mui/material'
import { Person, Devices as DevicesIcon, Edit, Search } from '@mui/icons-material'
import { useDevices, useDeviceAssignments, useAssignDevice, useUnassignDevice } from '../../hooks/useDevices'
import { useUsers } from '../../hooks/useUsers'
import { RoleGuard } from '../../components/RoleGuard'
import { PERMS } from '../../hooks/usePermissions'

interface DeviceLite { id: string; name: string; device_identifier: string; status: string }
interface UserLite   { id: string; name: string; email: string; roles?: string[] }

function unwrap<T>(d: unknown): T[] {
  if (Array.isArray(d)) return d as T[]
  const o = d as { devices?: T[]; data?: T[] } | undefined
  return o?.devices ?? o?.data ?? []
}

/**
 * Per-user assignment editor. Loads the active assignments across all devices
 * for the chosen user and exposes a checkbox grid for fast bulk-toggle.
 */
function EditDialog({
  open,
  user,
  devices,
  onClose,
}: {
  open: boolean
  user: UserLite | null
  devices: DeviceLite[]
  onClose: () => void
}) {
  const { t } = useTranslation()
  const assignmentQueries = devices.map((d) => useDeviceAssignments(d.id))
  const assign = useAssignDevice()
  const unassign = useUnassignDevice()

  const assignedMap = useMemo(() => {
    const map = new Map<string, string>() // deviceId -> assignmentId
    if (!user) return map
    devices.forEach((d, i) => {
      const list = assignmentQueries[i].data ?? []
      const a = list.find((x) => x.user_id === user.id && !x.unassigned_at)
      if (a) map.set(d.id, a.id)
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, devices, assignmentQueries.map((q) => q.data)])

  const handleToggle = async (deviceId: string) => {
    if (!user) return
    const existing = assignedMap.get(deviceId)
    if (existing) {
      await unassign.mutateAsync({ deviceId, assignmentId: existing })
      analytics.track(EVENTS.DEVICE_UNASSIGNED, { device_id: deviceId, user_id: user.id })
    } else {
      await assign.mutateAsync({ deviceId, userId: user.id })
      analytics.track(EVENTS.DEVICE_ASSIGNED, { device_id: deviceId, user_id: user.id })
    }
  }

  if (!user) return null

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('deviceAssignments.devicesVisibleTo', { name: user.name })}
        <Typography variant="caption" display="block" color="text.secondary">
          {user.email}
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ py: 1, maxHeight: 380 }}>
        {devices.length === 0 && <Alert severity="info">{t('deviceAssignments.noDevices')}</Alert>}
        <Stack>
          {devices.map((d) => {
            const checked = assignedMap.has(d.id)
            return (
              <FormControlLabel
                key={d.id}
                sx={{ py: 0.25 }}
                control={
                  <Checkbox
                    size="small"
                    checked={checked}
                    onChange={() => handleToggle(d.id)}
                    disabled={assign.isPending || unassign.isPending}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{d.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {d.device_identifier} · {d.status}
                    </Typography>
                  </Box>
                }
              />
            )
          })}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

/** A compact summary row showing how many devices are assigned to the user. */
function UserRow({
  user,
  devices,
  onEdit,
}: {
  user: UserLite
  devices: DeviceLite[]
  onEdit: (u: UserLite) => void
}) {
  const { t } = useTranslation()
  const queries = devices.map((d) => useDeviceAssignments(d.id))
  const visibleNames = useMemo(() => {
    const out: string[] = []
    devices.forEach((d, i) => {
      const list = queries[i].data ?? []
      if (list.some((a) => a.user_id === user.id && !a.unassigned_at)) {
        out.push(d.name)
      }
    })
    return out
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, devices, queries.map((q) => q.data)])

  return (
    <Card sx={{ p: 1.5, border: '1px solid #e2e8f0', borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
          <Avatar sx={{ bgcolor: '#EBF6FD', color: '#34A8E0', width: 36, height: 36 }}>
            <Person />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={700} noWrap>{user.name}</Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {user.email}
              {user.roles && user.roles.length > 0 && ' · ' + user.roles.join(', ')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, justifyContent: 'flex-end' }}>
          {visibleNames.length === 0 ? (
            <Chip label="all (admin) — or none" size="small" variant="outlined" />
          ) : (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end">
              {visibleNames.slice(0, 4).map((n) => (
                <Chip key={n} label={n} size="small"
                  sx={{ fontSize: 11, bgcolor: '#EBF6FD', color: '#34A8E0', border: '1px solid #B3DCF2' }} />
              ))}
              {visibleNames.length > 4 && (
                <Chip label={`+${visibleNames.length - 4}`} size="small" variant="outlined" />
              )}
            </Stack>
          )}
          <Tooltip title={t('deviceAssignments.editAssignments')}>
            <IconButton size="small" onClick={() => onEdit(user)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Card>
  )
}

export default function DeviceAssignmentsPage() {
  const { t } = useTranslation()
  const { data: devicesRaw, isLoading: dLoading } = useDevices()
  const { data: usersRaw, isLoading: uLoading } = useUsers()
  const devices = unwrap<DeviceLite>(devicesRaw)
  const users = unwrap<UserLite>(usersRaw)

  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [editTarget, setEditTarget] = useState<UserLite | null>(null)

  const allRoles = useMemo(() => {
    const s = new Set<string>()
    users.forEach((u) => (u.roles ?? []).forEach((r) => s.add(r)))
    return Array.from(s).sort()
  }, [users])

  const filtered = users.filter((u) => {
    const q = search.toLowerCase()
    const matchSearch = !q || u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    const matchRole = !roleFilter || (u.roles ?? []).includes(roleFilter)
    return matchSearch && matchRole
  })

  if (dLoading || uLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress sx={{ color: '#34A8E0' }} />
      </Box>
    )
  }

  return (
    <RoleGuard permissions={[PERMS.MANAGE_DEVICE_ASSIGNMENTS]}>
      <Box>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" fontWeight={800} color="#0f172a" sx={{ letterSpacing: '-0.02em' }}>
            {t('deviceAssignments.title')}
          </Typography>
          <Typography variant="body2" color="#94a3b8" sx={{ mt: 0.5 }}>
            {t('deviceAssignments.subtitle', { userCount: users.length, deviceCount: devices.length })}
          </Typography>
        </Box>

        <Card sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <TextField
              size="small" placeholder={t('deviceAssignments.searchPlaceholder')}
              value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1 }}
              InputProps={{ startAdornment: <Search sx={{ fontSize: 18, mr: 0.75, color: '#94a3b8' }} /> }}
            />
            <TextField select size="small" label={t('deviceAssignments.role')} value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)} sx={{ minWidth: 150 }}>
              <MenuItem value="">{t('deviceAssignments.allRoles')}</MenuItem>
              {allRoles.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
          </Stack>
        </Card>

        {devices.length === 0 && (
          <Alert severity="info" icon={<DevicesIcon />} sx={{ mb: 2 }}>
            {t('deviceAssignments.noDevicesRegistered')}
          </Alert>
        )}

        <Stack spacing={1.25}>
          {filtered.length === 0 && (
            <Card sx={{ p: 6, textAlign: 'center', border: '1px solid #e2e8f0', borderRadius: 2 }}>
              <Typography variant="body2" color="#94a3b8">{t('deviceAssignments.noUsersMatch')}</Typography>
            </Card>
          )}
          {filtered.map((u) => (
            <UserRow key={u.id} user={u} devices={devices} onEdit={setEditTarget} />
          ))}
        </Stack>

        <EditDialog
          open={!!editTarget}
          user={editTarget}
          devices={devices}
          onClose={() => setEditTarget(null)}
        />
      </Box>
    </RoleGuard>
  )
}
